import React, { useState, useEffect } from "react";
import ApplicationHistory from "../_common/ApplicationHistory";
import FeedbackPanel from "../_common/FeedbackPanel";
import SearchBox from "../_common/SearchBox";
import OfferStatus from "./OfferStatus";
import ProgressIndicator from "../_common/ProgressIndicator";
import SweetAlert from "react-bootstrap-sweetalert";
import InterviewFeedbackModal, { FeedbackData } from "src/components/interview-video/InterviewFeedbackModal";
import { useEmployerJobApi } from "src/_common/hooks/actions/job/appEmployerJobApiHook";
import { useScreeningApi } from "src/_common/hooks/actions/screening/appScreeningApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useJobListSelector } from "src/_common/hooks/selectors/jobSelector.";
import { CANDIDATE_STATUS } from "src/_config";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useAppUserDetailsSelector } from "src/_common/hooks/selectors/userSelector";

interface FinalDecisionProps {
  jobId?: string;
  jobData?: any;
  selectedCandidateId?: string | null;
  currentPage?: number;
  onPageChange?: (page: number) => void;
}

interface Candidate {
  id: number;
  name: string;
  position: string;
  category: "pending" | "hired" | "rejected";
  status: "hired" | "rejected" | "pending";
  bestMatch?: boolean;
  screeningId?: string; // Add screeningId to the interface
  latestScreeningHistory?: {
    status?: number;
    title?: string;
    description?: string;
  };
  latest_offer_history?: {
    status?: number;
    created_at?: string;
    title?: string;
    description?: string;
  };
  offer_letter_logs?: any[];
  interview_feedback_details?: Array<{
    screening_id?: string;
    company_user_id?: number;
    user_id?: number;
    attitude?: number;
    communication?: number;
    confidence?: number;
    cultural_fit?: number;
    problem_solving?: number;
    role_knowledge?: number;
    additional_comments?: string;
    overall_score?: number;
    created_at?: string;
    updated_at?: string;
    id?: string;
  }>;
}

interface SelectedCandidate {
  id: number;
  name: string;
  position: string;
  bestMatch: boolean;
  offerStatus: {
    sent: { date: string; completed: boolean };
    accepted: { waiting: boolean; completed: boolean };
    declined: { notApplicable: boolean; completed: boolean };
    status?: number;
  };
  fullCandidateData: Candidate | null;
}

export default function FinalDecision({
  jobId,
  jobData,
  selectedCandidateId,
  currentPage: propCurrentPage,
  onPageChange,
}: FinalDecisionProps) {
  const [activeTab, setActiveTab] = useState<"pending" | "hired" | "rejected">("pending");
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(propCurrentPage || 1);
  const [showInterviewFeedbackModal, setShowInterviewFeedbackModal] = useState(false);
  const [isViewModeInterviewFeedback, setIsViewModeInterviewFeedback] = useState(false);
  const { t } = useTranslation("job-details");
  const userDetails = useAppUserDetailsSelector();

  const jobListings = useJobListSelector();
  const currentJob = jobListings?.find((job) => job.emp_job_id == jobId);

  // Sync currentPage with parent component
  useEffect(() => {
    if (propCurrentPage && propCurrentPage !== currentPage) {
      setCurrentPage(propCurrentPage);
    }
  }, [propCurrentPage]);

  // Notify parent when current page changes
  useEffect(() => {
    if (onPageChange && currentPage !== (propCurrentPage || 1)) {
      onPageChange(currentPage);
    }
  }, [currentPage, onPageChange]);
  const [isLoading, setIsLoading] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [candidateToReject, setCandidateToReject] = useState<Candidate | null>(null);
  const [showHireConfirm, setShowHireConfirm] = useState(false);
  const [candidateToHire, setCandidateToHire] = useState<Candidate | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<SelectedCandidate | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [finalDecisionPagination, setFinalDecisionPagination] = useState<any>(null);
  const itemsPerPage = 10;
  const [statusCounts, setStatusCounts] = useState<any>({});

  // API hooks
  const employerJobApi = useEmployerJobApi();
  const screeningApi = useScreeningApi();
  const toast = useToaster();

  const handleCandidateSelect = (candidate: Candidate, candidatesArray?: Candidate[]) => {
    // Find the full candidate data with latest_offer_history
    // Use provided candidatesArray if available, otherwise use current candidates state
    const searchArray = candidatesArray || candidates;
    const fullCandidate = searchArray.find((c) => c.id === candidate.id);
    const latestOfferHistory = (fullCandidate as any)?.latest_offer_history;

    // Determine offer status based on latest_offer_history
    let offerStatus = {
      sent: { date: "", completed: false },
      accepted: { waiting: false, completed: false },
      declined: { notApplicable: true, completed: false },
      status: undefined,
    };

    if (latestOfferHistory) {
      const status = latestOfferHistory.status;
      const offerDate = latestOfferHistory.created_at
        ? new Date(latestOfferHistory.created_at).toLocaleDateString()
        : "";

      if (status === 6) {
        // Offer sent
        offerStatus = {
          sent: { date: offerDate, completed: true },
          accepted: { waiting: true, completed: false },
          declined: { notApplicable: false, completed: false },
          status,
        };
      } else if (status === 7) {
        // Offer accepted
        offerStatus = {
          sent: { date: offerDate, completed: true },
          accepted: { waiting: false, completed: true },
          declined: { notApplicable: true, completed: false },
          status,
        };
      } else if (status === 8) {
        // Offer declined
        offerStatus = {
          sent: { date: offerDate, completed: true },
          accepted: { waiting: false, completed: false },
          declined: { notApplicable: false, completed: true },
          status,
        };
      }
    }

    setSelectedCandidate({
      id: candidate.id,
      name: candidate.name,
      position: candidate.position,
      bestMatch: candidate.bestMatch || false,
      offerStatus,
      fullCandidateData: fullCandidate ?? null,
    });
  };

  // Fetch final decision candidates data from API
  const fetchFinalDecisionCandidates = () => {
    if (!jobId) {
      console.warn("Job ID is not available");
      return;
    }

    setIsLoading(true);

    // Prepare additional parameters for search
    const additionalParams: any = {};
    if (searchTerm && searchTerm.trim()) {
      additionalParams.search = searchTerm.trim();
    }

    // Set status based on active tab
    if (activeTab === "pending") {
      additionalParams.status = [CANDIDATE_STATUS.FINAL_DECISION];
    } else if (activeTab === "hired") {
      additionalParams.status = [CANDIDATE_STATUS.HIRED];
    } else if (activeTab === "rejected") {
      additionalParams.status = [CANDIDATE_STATUS.INTERVIEW_REJECTED];
    }

    employerJobApi.callGetScreening(
      jobId,
      itemsPerPage,
      (currentPage - 1) * itemsPerPage,
      additionalParams,
      (message: string, resp: any) => {
        console.log("Final decision screening data fetched successfully:", resp);

        // Transform and format the screening data for final decision stage
        if (resp && resp.screenings) {
          const formattedCandidates = resp.screenings.map((screening: any) => {
            // Extract candidate name from the new jobseeker_details structure
            let candidateName = "";

            if (screening.jobseeker_details && screening.jobseeker_details.length > 0) {
              const jobseekerDetails = screening.jobseeker_details[0];

              if (jobseekerDetails.basic_details && jobseekerDetails.basic_details.length > 0) {
                const basicDetails = jobseekerDetails.basic_details[0];

                if (basicDetails.name && basicDetails.name.trim()) {
                  candidateName = basicDetails.name.trim();
                } else if (basicDetails.first_name && basicDetails.last_name) {
                  candidateName = `${basicDetails.first_name} ${basicDetails.last_name}`.trim();
                } else if (basicDetails.first_name) {
                  candidateName = basicDetails.first_name.trim();
                } else {
                  candidateName = "N/A";
                }
              } else {
                candidateName = "N/A";
              }
            } else {
              candidateName = "N/A";
            }

            // Determine status and category based on latest_screening_history
            let candidateStatus: "hired" | "rejected" | "pending" = "pending";
            let candidateCategory: "pending" | "hired" | "rejected" = "pending";

            if (screening.latest_screening_history) {
              const historyStatus = screening.latest_screening_history.status;
              if (historyStatus === 2) {
                candidateStatus = "rejected";
                candidateCategory = "rejected";
              } else if (historyStatus === 3) {
                candidateStatus = "hired";
                candidateCategory = "hired";
              }
            }

            return {
              id: screening.id,
              name: candidateName,
              // position:
              //   screening?.jobseeker_details?.[0]?.experience &&
              //   Array.isArray(screening?.jobseeker_details?.[0]?.experience)
              //     ? screening?.jobseeker_details?.[0]?.experience
              //         .map((exp: any) => exp.designation)
              //         .filter(Boolean)
              //         .join(", ")
              //     : "-",
              position: currentJob?.job_title,
              category: candidateCategory,
              status: candidateStatus,
              screeningId: screening.id,
              latestScreeningHistory: screening.latest_screening_history || null,
              latest_offer_history: screening.latest_offer_history || null,
              offer_letter_logs: screening.offer_letter_logs || null,
              jobseeker_details: screening.jobseeker_details || null,
              interview_feedback_details: screening.interview_feedback_details || [],
            };
          });

          setCandidates(formattedCandidates);

          // Set first candidate as selected if none selected, or update selected candidate if it exists
          if (!selectedCandidate && formattedCandidates.length > 0) {
            handleCandidateSelect(formattedCandidates[0], formattedCandidates);
          } else if (selectedCandidate) {
            // Update the selected candidate with fresh data if it still exists
            const updatedSelectedCandidate = formattedCandidates.find((c: Candidate) => c.id === selectedCandidate.id);
            if (updatedSelectedCandidate) {
              handleCandidateSelect(updatedSelectedCandidate, formattedCandidates);
            } else {
              // If selected candidate no longer exists, select the first one
              if (formattedCandidates.length > 0) {
                handleCandidateSelect(formattedCandidates[0], formattedCandidates);
              } else {
                setSelectedCandidate(null);
              }
            }
          }
        } else {
          setCandidates([]);
          setSelectedCandidate(null);
        }

        if (resp && resp.pagination) {
          setFinalDecisionPagination(resp.pagination);
        }

        setIsLoading(false);
      },
      (error: string) => {
        console.error("Error fetching final decision candidates:", error);
        toast.error(error);
        setIsLoading(false);
      }
    );
  };

  const fetchAllJobStatus = () => {
    employerJobApi.callGetJobAllStatusCounts(
      { job_ids: [jobId + ""], matching_percentage: 50 },
      (message: string, resp: any) => {
        setStatusCounts(resp?.status_counts || {});
      },
      (error: string) => {}
    );
  };

  // useEffect to call API on component mount and when dependencies change
  useEffect(() => {
    if (jobId) {
      fetchFinalDecisionCandidates();
      fetchAllJobStatus();
    }
  }, [currentPage, jobId, searchTerm, activeTab]);

  // useEffect to automatically select the candidate from interview
  useEffect(() => {
    if (selectedCandidateId && candidates?.length > 0) {
      // Find if the selected candidate exists in the current final decision candidates
      const candidateExists = candidates.find((c) => c.id.toString() === selectedCandidateId.toString());
      if (candidateExists) {
        console.log("Selecting candidate in final decision:", selectedCandidateId, candidateExists);
        handleCandidateSelect(candidateExists);
      } else {
        console.log("Candidate not found in current page, might be on different page:", selectedCandidateId);
        // The candidate might be on a different page, but we've already set the correct page from parent
        // For now, just keep the selected state
      }
    }
  }, [selectedCandidateId, candidates]);

  const getTabCounts = () => {
    return {
      all: statusCounts?.final_decision || 0,
      hired: statusCounts?.hired || 0,
      rejected: statusCounts?.interview_rejected || 0,
    };
  };

  const filteredCandidates = candidates.filter((candidate) => {
    // Filter by tab selection based on candidate status
    if (activeTab === "pending") return true;
    if (activeTab === "hired") return candidate.status === "hired";
    if (activeTab === "rejected") return candidate.status === "rejected";
    return true;
  });

  const tabCounts = getTabCounts();

  const handleTabChange = (tab: "pending" | "hired" | "rejected") => {
    setActiveTab(tab);
    // Clear the selected candidate when switching tabs
    setSelectedCandidate(null);
    setCurrentPage(1);
    // Note: The first candidate will be auto-selected by useEffect after data is fetched
  };

  const handleRejectCandidate = () => {
    if (selectedCandidate) {
      const candidate = candidates.find((c) => c.id === selectedCandidate.id);
      if (candidate) {
        setCandidateToReject(candidate);
        setShowRejectConfirm(true);
      }
    }
  };

  const handleConfirmReject = () => {
    if (candidateToReject) {
      // Call addhistory API with rejection parameters
      const payload = {
        ...(userDetails?.user_type != "member" ? { member_id: userDetails?.user?.id } : {}),
        screening_id: candidateToReject.screeningId,
        application_type: 1,
        status: 2,
        title: t("jobDetails.finalDecision.apiPayload.rejectTitle"),
        description: t("jobDetails.finalDecision.apiPayload.rejectDescription"),
      };

      screeningApi.callAddFeedback(
        payload,
        (message: string, resp: any) => {
          console.log("Candidate rejection recorded successfully:", resp);
          toast.success(t("jobDetails.finalDecision.messages.rejectSuccess"));

          // Also update candidate status to REJECTED
          screeningApi.callUpdateStatus(
            {
              screening_id: candidateToReject?.screeningId,
              status: CANDIDATE_STATUS.INTERVIEW_REJECTED,
            },
            (message: string, resp: any) => {
              // status updated
              // Switch to rejected tab to show the rejected candidate
              setActiveTab("rejected");
              fetchFinalDecisionCandidates();
            },
            (error: string) => {}
          );
        },
        (error: string) => {
          console.error("Error recording candidate rejection:", error);
          toast.error(t("jobDetails.finalDecision.messages.rejectError") + error);
        }
      ); // Close the modal

      setShowRejectConfirm(false);
      setCandidateToReject(null);
    }
  };

  const handleCancelReject = () => {
    setShowRejectConfirm(false);
    setCandidateToReject(null);
  };

  const handleGetHire = () => {
    if (selectedCandidate) {
      const candidate = candidates.find((c) => c.id === selectedCandidate.id);
      if (candidate) {
        setCandidateToHire(candidate);
        setShowHireConfirm(true);
      }
    }
  };

  const handleConfirmHire = () => {
    if (candidateToHire) {
      // Call addhistory API with hire parameters
      const payload = {
        ...(userDetails?.user_type != "member" ? { member_id: userDetails?.user?.id } : {}),
        screening_id: candidateToHire.screeningId,
        application_type: 1,
        status: 3,
        title: t("jobDetails.finalDecision.apiPayload.hireTitle"),
        description: t("jobDetails.finalDecision.apiPayload.hireDescription"),
      };

      screeningApi.callAddFeedback(
        payload,
        (message: string, resp: any) => {
          console.log("Candidate hire recorded successfully:", resp);
          toast.success(t("jobDetails.finalDecision.messages.hireSuccess"));
          screeningApi.callUpdateStatus(
            {
              screening_id: candidateToHire?.screeningId,
              status: CANDIDATE_STATUS.HIRED,
            },
            (message: string, resp: any) => {
              // status updated
              // Switch to hired tab to show the hired candidate
              setActiveTab("hired");
              fetchFinalDecisionCandidates();
            },
            (error: string) => {}
          );
          // Optionally refresh the final decision data
        },
        (error: string) => {
          console.error("Error recording candidate hire:", error);
          toast.error(t("jobDetails.finalDecision.messages.hireError") + error);
        }
      );

      // Close the modal
      setShowHireConfirm(false);
      setCandidateToHire(null);
    }
  };

  const handleCancelHire = () => {
    setShowHireConfirm(false);
    setCandidateToHire(null);
  };

  const handleAddFeedback = () => {
    setIsFeedbackOpen(true);
  };

  const handleViewInterviewFeedback = () => {
    setIsViewModeInterviewFeedback(true);
    setShowInterviewFeedbackModal(true);
  };

  const handleInterviewFeedbackCancel = () => {
    setShowInterviewFeedbackModal(false);
    setIsViewModeInterviewFeedback(false);
  };

  const handleInterviewFeedbackSubmit = (feedbackData: FeedbackData) => {
    setShowInterviewFeedbackModal(false);
    fetchFinalDecisionCandidates(); // Refresh the list
  };

  const handleViewHistory = () => {
    if (selectedCandidate && selectedCandidate.id) {
      // Find the candidate with screening ID from our candidates list
      const candidate = candidates.find((c) => c.id === selectedCandidate.id);

      if (candidate && candidate.screeningId) {
        // Open the ApplicationHistory panel - it will handle the API call internally
        setIsHistoryOpen(true);
      } else {
        console.warn("No screening ID found for selected candidate:", selectedCandidate.id);
        toast.error(t("jobDetails.finalDecision.messages.noScreeningId"));
      }
    }
  };

  const handleCloseHistory = () => {
    setIsHistoryOpen(false);
  };

  const handleCloseFeedback = () => {
    setIsFeedbackOpen(false);
  };

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1); // Reset to first page when clearing search
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    const maxPages = Math.ceil((finalDecisionPagination?.total || 0) / itemsPerPage);

    if (page >= 1 && page <= maxPages) {
      setCurrentPage(page);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const maxPages = Math.ceil((finalDecisionPagination?.total || 0) / itemsPerPage);

    if (currentPage < maxPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Generate page numbers for display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    const maxPages = Math.ceil((finalDecisionPagination?.total || 0) / itemsPerPage);

    if (maxPages <= maxVisiblePages) {
      for (let i = 1; i <= maxPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(maxPages);
      } else if (currentPage >= maxPages - 3) {
        pages.push(1);
        pages.push("...");
        for (let i = maxPages - 4; i <= maxPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(maxPages);
      }
    }

    return pages;
  };

  return (
    <div className="final-decision-wrapper">
      {/* Header */}
      <div className="final-decision-header">
        <div className="header-left-ai-job">
          <div className="job-overview-section">
            <h1 className="job-overview-title">{currentJob?.job_title}</h1>
            <p className="job-overview-subtitle">{t("jobDetails.finalDecision.subtitle")}</p>
          </div>
        </div>
        {/* Only show search when there are candidates or search is active or loading */}
        {!(
          !isLoading &&
          candidates?.length === 0 &&
          !searchTerm &&
          tabCounts.all == 0 &&
          tabCounts.hired == 0 &&
          tabCounts.rejected == 0
        ) && (
          <div className="header-search">
            <SearchBox
              placeholder={t("jobDetails.searchBox.placeholder")}
              onSearch={handleSearch}
              onClear={handleClearSearch}
            />
          </div>
        )}
      </div>

      {/* Show ProgressIndicator when no candidates and no search */}
      {!isLoading &&
      candidates?.length === 0 &&
      !searchTerm &&
      tabCounts.all == 0 &&
      tabCounts.hired == 0 &&
      tabCounts.rejected == 0 ? (
        <ProgressIndicator stage="final-decision" />
      ) : (
        <div className="final-decision-main">
          {/* Candidates Section */}
          <div className="candidates-section">
            <div className="candidates-header">
              <h3>{t("jobDetails.finalDecision.candidates")}</h3>
              <p className="candidates-subtitle">{t("jobDetails.finalDecision.subtitle")}</p>
            </div>

            {/* Tab Navigation */}
            <div className="view-switcher">
              <button
                className={`view-btn ${activeTab === "pending" ? "active" : ""}`}
                onClick={() => handleTabChange("pending")}
              >
                {t("jobDetails.finalDecision.tabs.pending")} ({tabCounts.all})
              </button>
              <button
                className={`view-btn ${activeTab === "hired" ? "active" : ""}`}
                onClick={() => handleTabChange("hired")}
              >
                {t("jobDetails.finalDecision.tabs.hired")} ({tabCounts.hired})
              </button>
              <button
                className={`view-btn ${activeTab === "rejected" ? "active" : ""}`}
                onClick={() => handleTabChange("rejected")}
              >
                {t("jobDetails.finalDecision.tabs.rejected")} ({tabCounts.rejected})
              </button>
            </div>

            {/* Candidates List */}
            <div className="candidates-list">
              {isLoading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <span>{t("jobDetails.finalDecision.loadingCandidates")}</span>
                </div>
              ) : filteredCandidates.length > 0 ? (
                filteredCandidates.map((candidate) => (
                  <div
                    key={candidate.id}
                    className={`candidate-item ${selectedCandidate?.id === candidate.id ? "selected" : ""} ${
                      candidate.status
                    }`}
                    onClick={() => handleCandidateSelect(candidate)}
                  >
                    <div className="candidate-avatar">{candidate.name.charAt(0)}</div>
                    <div className="candidate-details">
                      <div className="candidate-name-section">
                        <h4>{candidate.name}</h4>
                        {candidate.bestMatch && (
                          <span className="best-match-tag">{t("jobDetails.finalDecision.bestMatch")}</span>
                        )}
                      </div>
                      <p className="candidate-role">
                        {candidate?.position
                          ? t("jobDetails.finalDecision.applyingFor") + " " + candidate.position
                          : ""}
                      </p>
                    </div>
                    {candidate.status === "rejected" && (
                      <span className="status-badge rejected">{t("jobDetails.finalDecision.tabs.rejected")}</span>
                    )}
                    {candidate.status === "hired" && (
                      <span className="status-badge hired">{t("jobDetails.finalDecision.tabs.hired")}</span>
                    )}
                  </div>
                ))
              ) : (
                <div style={{ padding: "40px 20px", textAlign: "center", color: "#666" }}>
                  {t("jobDetails.finalDecision.noCandidates")}
                </div>
              )}
            </div>

            {/* Pagination */}
            {filteredCandidates.length > 0 && Math.ceil((finalDecisionPagination?.total || 0) / itemsPerPage) > 1 && (
              <div className="pagination">
                <button className="page-btn" onClick={handlePrevPage} disabled={currentPage === 1}>
                  {t("jobDetails.finalDecision.pagination.prev")}
                </button>
                <div className="page-numbers">
                  {getPageNumbers().map((page, index) =>
                    page === "..." ? (
                      <span key={`ellipsis-${index}`}>...</span>
                    ) : (
                      <button
                        key={page}
                        className={`page-num ${currentPage === page ? "active" : ""}`}
                        onClick={() => handlePageChange(page as number)}
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>
                <button
                  className="page-btn"
                  onClick={handleNextPage}
                  disabled={currentPage === Math.ceil((finalDecisionPagination?.total || 0) / itemsPerPage)}
                >
                  {t("jobDetails.finalDecision.pagination.next")}
                </button>
              </div>
            )}
          </div>

          {/* Selected Candidate Details */}
          {selectedCandidate && (
            <div className="candidate-details-section">
              <div className="candidate-profile">
                <div className="profile-header">
                  <div className="profile-info">
                    <div className="name-and-badge">
                      <h2>{selectedCandidate.name}</h2>
                      {(() => {
                        const candidate = candidates.find((c) => c.id === selectedCandidate.id);
                        return (
                          <>
                            {candidate?.latestScreeningHistory?.status == 2 && (
                              <span className="status-badge rejected">
                                {t("jobDetails.finalDecision.tabs.rejected")}
                              </span>
                            )}
                            {candidate?.latestScreeningHistory?.status == 3 && (
                              <span className="status-badge hired">{t("jobDetails.finalDecision.tabs.hired")}</span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <p>
                      {selectedCandidate?.position
                        ? t("jobDetails.finalDecision.applyingFor") + " " + selectedCandidate.position
                        : ""}
                    </p>
                  </div>
                  <div className="profile-actions">
                    {selectedCandidate?.fullCandidateData?.interview_feedback_details &&
                      selectedCandidate.fullCandidateData.interview_feedback_details.length > 0 && (
                        <button className="btn-cancel" onClick={handleViewInterviewFeedback}>
                          <i className="fa fa-eye"></i>
                          View Live Interview Report
                        </button>
                      )}
                    <button className="btn-cancel" onClick={handleAddFeedback}>
                      <i className="fa fa-plus"></i>
                      {t("jobDetails.finalDecision.buttons.addFeedback")}
                    </button>
                    <button className="btn-cancel" onClick={handleViewHistory}>
                      <i className="fa fa-clock"></i>
                      {t("jobDetails.finalDecision.buttons.viewHistory")}
                    </button>
                  </div>
                </div>

                {/* Action Buttons */}

                {(() => {
                  const candidate = candidates.find((c) => c.id === selectedCandidate.id);
                  return (
                    candidate?.latestScreeningHistory?.status == 3 &&
                    selectedCandidate && (
                      <OfferStatus
                        selectedCandidate={selectedCandidate}
                        fetchFinalDecisionCandidates={fetchFinalDecisionCandidates}
                      />
                    )
                  );
                })()}
                {/* Hide reject button if candidate is already rejected (status = 2) */}
                {(() => {
                  const candidate = candidates.find((c) => c.id === selectedCandidate.id);
                  return (
                    candidate?.latestScreeningHistory?.status != 2 &&
                    candidate?.latestScreeningHistory?.status != 3 && (
                      // return candidate?.latestScreeningHistory?.status == 2 ? (
                      //   <div className="decision-actions">
                      //     <button className="btn-reject" disabled>
                      //       <i className="fa fa-times"></i>
                      //       Candidate Rejected
                      //     </button>
                      //   </div>
                      // ) : candidate?.latestScreeningHistory?.status == 3 ? (
                      //   <>
                      //     <OfferStatus selectedCandidate={selectedCandidate} />
                      //     <div className="decision-actions">
                      //       <button className="btn-reject btn-get-hire" disabled>
                      //         <i className="fa fa-check"></i>
                      //         Candidate Hired
                      //       </button>
                      //     </div>
                      //   </>
                      // ) :

                      <div className="decision-actions">
                        <button className="btn-reject" onClick={handleRejectCandidate}>
                          <i className="fa fa-times"></i>
                          {t("jobDetails.finalDecision.buttons.rejectCandidate")}
                        </button>
                        <button className="btn-get-hire" onClick={handleGetHire}>
                          <i className="fa fa-check"></i>
                          {t("jobDetails.finalDecision.buttons.getHire")}
                        </button>
                      </div>
                    )
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Application History Sidebar */}
      <ApplicationHistory
        candidateName={selectedCandidate?.name || ""}
        screeningId={selectedCandidate ? candidates.find((c) => c.id === selectedCandidate.id)?.screeningId : undefined}
        isOpen={isHistoryOpen}
        onClose={handleCloseHistory}
      />

      {/* Feedback Panel */}
      <FeedbackPanel
        candidateName={selectedCandidate?.name || ""}
        screeningId={
          selectedCandidate
            ? candidates.find((c) => c.id === selectedCandidate.id)?.screeningId ||
              `SCR_${Date.now()}_${selectedCandidate.id}`
            : ""
        }
        isOpen={isFeedbackOpen}
        onClose={handleCloseFeedback}
      />

      {/* Hire Confirmation Modal */}
      {showHireConfirm && candidateToHire && (
        <SweetAlert
          success
          showCancel
          confirmBtnText={t("jobDetails.finalDecision.confirmHire.confirmButton")}
          confirmBtnBsStyle="preview"
          cancelBtnText={t("jobDetails.finalDecision.confirmHire.cancelButton")}
          cancelBtnBsStyle="cancel"
          title={t("jobDetails.finalDecision.confirmHire.title")}
          onConfirm={handleConfirmHire}
          onCancel={handleCancelHire}
        >
          {t("jobDetails.finalDecision.confirmHire.message1")} <strong>{candidateToHire.name}</strong>?
          <br />
          <br />
          {t("jobDetails.finalDecision.confirmHire.message2")}
        </SweetAlert>
      )}

      {/* Reject Confirmation Modal */}
      {showRejectConfirm && candidateToReject && (
        <SweetAlert
          warning
          showCancel
          confirmBtnText={t("jobDetails.finalDecision.confirmReject.confirmButton")}
          confirmBtnBsStyle="preview"
          cancelBtnText={t("jobDetails.finalDecision.confirmReject.cancelButton")}
          cancelBtnBsStyle="cancel"
          title={t("jobDetails.finalDecision.confirmReject.title")}
          onConfirm={handleConfirmReject}
          onCancel={handleCancelReject}
        >
          {t("jobDetails.finalDecision.confirmReject.message1")} <strong>{candidateToReject.name}</strong>?
          <br />
          <br />
          {t("jobDetails.finalDecision.confirmReject.message2")}
        </SweetAlert>
      )}

      {/* Interview Feedback Modal */}
      {showInterviewFeedbackModal && selectedCandidate?.fullCandidateData && (
        <InterviewFeedbackModal
          isVisible={showInterviewFeedbackModal}
          onSubmit={handleInterviewFeedbackSubmit}
          onCancel={handleInterviewFeedbackCancel}
          screeningId={String(selectedCandidate.id)}
          isViewMode={isViewModeInterviewFeedback}
          existingFeedback={
            isViewModeInterviewFeedback &&
            selectedCandidate.fullCandidateData.interview_feedback_details &&
            selectedCandidate.fullCandidateData.interview_feedback_details.length > 0
              ? {
                  attitude: selectedCandidate.fullCandidateData.interview_feedback_details[0].attitude || 0,
                  communication: selectedCandidate.fullCandidateData.interview_feedback_details[0].communication || 0,
                  confidence: selectedCandidate.fullCandidateData.interview_feedback_details[0].confidence || 0,
                  culturalFit: selectedCandidate.fullCandidateData.interview_feedback_details[0].cultural_fit || 0,
                  problemSolving:
                    selectedCandidate.fullCandidateData.interview_feedback_details[0].problem_solving || 0,
                  roleKnowledge: selectedCandidate.fullCandidateData.interview_feedback_details[0].role_knowledge || 0,
                  additionalComments:
                    selectedCandidate.fullCandidateData.interview_feedback_details[0].additional_comments || "",
                }
              : undefined
          }
        />
      )}
    </div>
  );
}
